import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MatchInfoDialog from "@/components/MatchInfoDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as ShadCalendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/utils/dateFormat";
import { CalendarDays, Target, Trophy, ArrowRight, Clock, Shield, MapPin, CalendarPlus, Copy, Download, Printer, Info, Calendar as CalendarIcon } from "lucide-react";

const API = "/api";

function parseDT(s) {
  if (!s) return null;
  const d = new Date(s.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatTime(dt) {
  const d = parseDT(dt);
  if (!d) return "TBD";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function MatchCalendar() {
  const { isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("all");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventSelectedDate, setEventSelectedDate] = useState(new Date());
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  // Build the subscription URL based on current event filter
  const subscribeUrl = useMemo(() => {
    const base = window.location.origin + "/api/calendar.ics";
    return selectedEventId === "all" ? base : `${base}?event_id=${encodeURIComponent(selectedEventId)}`;
  }, [selectedEventId]);

  // webcal:// allows one-click subscription in many OS calendars
  const webcalUrl = useMemo(() => {
    return subscribeUrl.replace(/^https?:\/\//, "webcal://");
  }, [subscribeUrl]);

  const copySubscribeUrl = async () => {
    try {
      await navigator.clipboard.writeText(subscribeUrl);
      toast.success("Calendar URL copied to clipboard");
    } catch {
      toast.error("Copy failed — select the URL and copy manually");
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const t = Date.now();
        const res = await axios.get(`${API}/events?t=${t}`);
        setEvents(res.data || []);
      } catch (e) {
        console.error("Failed to load events", e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadMatches = async () => {
      setLoading(true);
      try {
        const t = Date.now();
        let targetEvents = [];
        if (selectedEventId === "all") {
          targetEvents = events.filter((e) => e.status !== "cancelled");
        } else {
          const ev = events.find((e) => e.id === selectedEventId);
          if (ev) targetEvents = [ev];
        }
        const detailResponses = await Promise.all(
          targetEvents.map((e) => axios.get(`${API}/events/${e.id}?t=${t}`).then((r) => r.data).catch(() => null))
        );
        const all = [];
        detailResponses.forEach((ev) => {
          if (!ev) return;
          (ev.matches || []).forEach((m) => {
            if (m.start_time) {
              all.push({ ...m, event_id: ev.id, event_name: ev.name });
            }
          });
        });
        setMatches(all);
      } catch (e) {
        console.error("Failed to load matches", e);
      } finally {
        setLoading(false);
      }
    };
    if (events.length > 0 || selectedEventId !== "all") loadMatches();
    else setLoading(false);
  }, [events, selectedEventId]);

  // Helper: array of midnight Dates covering every day from start_time to end_time inclusive.
  // Falls back to a single day when end_time is missing or equal to start_time.
  const expandMatchDays = (m) => {
    const start = parseDT(m.start_time);
    if (!start) return [];
    const end = parseDT(m.end_time) || start;
    const days = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    let guard = 0;
    while (cur <= endDay && guard < 366) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
      guard += 1;
    }
    return days;
  };

  // Compute days that have at least one match (including multi-day matches)
  const matchDays = useMemo(() => {
    const set = new Set();
    matches.forEach((m) => {
      expandMatchDays(m).forEach((d) => {
        set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      });
    });
    return set;
  }, [matches]);

  const matchedDayModifier = useMemo(() => {
    const all = [];
    matches.forEach((m) => {
      expandMatchDays(m).forEach((d) => all.push(d));
    });
    return all;
  }, [matches]);

  const matchesForSelectedDay = useMemo(() => {
    if (!selectedDate) return [];
    return matches
      .filter((m) => {
        const days = expandMatchDays(m);
        return days.some((d) => sameDay(d, selectedDate));
      })
      .sort((a, b) => (parseDT(a.start_time)?.getTime() ?? 0) - (parseDT(b.start_time)?.getTime() ?? 0));
  }, [matches, selectedDate]);

  // -------- EVENTS CALENDAR DATA --------
  const eventsForCalendar = useMemo(() => {
    if (selectedEventId === "all") {
      return events.filter((e) => e.status !== "cancelled" && e.start_date);
    }
    const ev = events.find((e) => e.id === selectedEventId);
    return ev ? [ev] : [];
  }, [events, selectedEventId]);

  // Array of Date objects (one per day) covering each event's date range
  const eventDateModifier = useMemo(() => {
    const days = [];
    eventsForCalendar.forEach((ev) => {
      const start = ev.start_date ? new Date(ev.start_date + "T00:00:00") : null;
      const end = ev.end_date ? new Date(ev.end_date + "T00:00:00") : start;
      if (!start || isNaN(start.getTime())) return;
      const endD = end && !isNaN(end.getTime()) ? end : start;
      const cur = new Date(start);
      let guard = 0;
      while (cur <= endD && guard < 366) {
        days.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
    });
    return days;
  }, [eventsForCalendar]);

  const eventsForSelectedDay = useMemo(() => {
    if (!eventSelectedDate) return [];
    return eventsForCalendar.filter((ev) => {
      const start = ev.start_date ? new Date(ev.start_date + "T00:00:00") : null;
      const end = ev.end_date ? new Date(ev.end_date + "T00:00:00") : start;
      if (!start || isNaN(start.getTime())) return false;
      const endD = end && !isNaN(end.getTime()) ? end : start;
      const day = new Date(eventSelectedDate.getFullYear(), eventSelectedDate.getMonth(), eventSelectedDate.getDate());
      return day >= new Date(start.getFullYear(), start.getMonth(), start.getDate())
        && day <= new Date(endD.getFullYear(), endD.getMonth(), endD.getDate());
    });
  }, [eventsForCalendar, eventSelectedDate]);

  return (
    <div data-testid="match-calendar-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Match Calendar
          </h1>
          <p className="text-slate-500 mt-1">Click a highlighted date to view matches and times</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
          <div className="w-full sm:w-64">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger data-testid="calendar-event-select">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                {events.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.name} ({formatDate(ev.start_date)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => setSubscribeOpen(true)}
            data-testid="subscribe-calendar-btn"
            className="border-[#1B3B6F] text-[#1B3B6F] hover:bg-[#1B3B6F] hover:text-white"
          >
            <CalendarPlus size={16} className="mr-2" />
            Subscribe
          </Button>
          {selectedEventId !== "all" && (
            <Link to={`/print-schedule/${selectedEventId}`} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="border-[#1B3B6F] text-[#1B3B6F] hover:bg-[#1B3B6F] hover:text-white"
                data-testid="calendar-print-btn"
              >
                <Printer size={16} className="mr-2" />
                Print
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <CalendarIcon size={18} className="text-emerald-700" />
              Events Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ShadCalendar
              mode="single"
              selected={eventSelectedDate}
              onSelect={setEventSelectedDate}
              modifiers={{ hasEvent: eventDateModifier }}
              modifiersClassNames={{
                hasEvent: "bg-emerald-100 text-emerald-900 font-semibold ring-1 ring-emerald-300",
              }}
              data-testid="events-calendar-widget"
            />
            <div className="mt-4 text-xs text-slate-500 space-y-1">
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded bg-emerald-100 ring-1 ring-emerald-300" />
                Days inside an event's date range
              </div>
              {!loading && (
                <div>
                  {eventsForCalendar.length} event(s){selectedEventId === "all" ? "" : " selected"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <CalendarIcon size={18} className="text-emerald-700" />
              {eventSelectedDate
                ? eventSelectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "Select a date"}
              <Badge variant="outline" className="ml-2">{eventsForSelectedDay.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {eventsForSelectedDay.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <CalendarIcon className="mx-auto text-slate-300 mb-2" size={32} />
                No events on this day.
              </div>
            ) : (
              <div className="divide-y">
                {eventsForSelectedDay.map((ev) => (
                  <Link
                    key={ev.id}
                    to={`/event-info/${ev.id}`}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`events-cal-event-${ev.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-800">{ev.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatDate(ev.start_date)}
                        {ev.end_date && ev.end_date !== ev.start_date ? ` – ${formatDate(ev.end_date)}` : ""}
                        {ev.match_count != null ? ` • ${ev.match_count} matches` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Info size={16} className="text-slate-400" />
                      <ArrowRight size={16} className="text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <CalendarDays size={18} className="text-[#1B3B6F]" />
              Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ShadCalendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              modifiers={{ hasMatch: matchedDayModifier }}
              modifiersClassNames={{
                hasMatch: "bg-amber-100 text-amber-900 font-semibold ring-1 ring-amber-300",
              }}
              data-testid="calendar-widget"
            />
            <div className="mt-4 text-xs text-slate-500 space-y-1">
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded bg-amber-100 ring-1 ring-amber-300" />
                Days with scheduled matches
              </div>
              {loading && <div>Loading matches…</div>}
              {!loading && <div>{matchDays.size} day(s) with matches</div>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Clock size={18} className="text-[#1B3B6F]" />
              {selectedDate
                ? selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "Select a date"}
              <Badge variant="outline" className="ml-2">{matchesForSelectedDay.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {matchesForSelectedDay.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <Target className="mx-auto text-slate-300 mb-2" size={32} />
                No matches scheduled for this day.
              </div>
            ) : (
              <div className="divide-y">
                {matchesForSelectedDay.map((match) => (
                  <div
                    key={match.id}
                    className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-slate-50"
                    data-testid={`calendar-match-${match.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center justify-center min-w-[72px] bg-[#1B3B6F]/10 text-[#1B3B6F] rounded-lg px-2 py-2">
                        <Clock size={14} />
                        <span className="font-mono font-semibold text-sm mt-1">{formatTime(match.start_time)}</span>
                      </div>
                      <div className={`p-2 rounded-lg ${match.is_protection ? "bg-purple-100" : "bg-green-100"}`}>
                        {match.is_protection ? (
                          <Shield className="text-purple-600" size={20} />
                        ) : (
                          <Target className="text-green-600" size={20} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          <MatchInfoDialog match={match} eventName={match.event_name} prefix />
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">{match.event_name}</p>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                          {match.is_protection && (
                            <Badge variant="outline" className="text-purple-600">Protection</Badge>
                          )}
                          <span>{match.shots_per_round} shots</span>
                          {match.trap_number && (
                            <span className="flex items-center gap-1">
                              <MapPin size={10} /> Trap {match.trap_number}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <Link to={`/leaderboard/${match.id}`}>
                        <Button variant="outline" size="sm">
                          <Trophy size={14} className="mr-1" />
                          Leaderboard
                        </Button>
                      </Link>
                      {isAdmin() && (
                        <Link to={`/score-entry/${match.id}`}>
                          <Button size="sm" className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                            Enter Scores
                            <ArrowRight size={14} className="ml-1" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscribe Dialog */}
      <Dialog open={subscribeOpen} onOpenChange={setSubscribeOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <CalendarPlus className="text-[#1B3B6F]" />
              Subscribe to Match Calendar
            </DialogTitle>
            <DialogDescription>
              {selectedEventId === "all"
                ? "Subscribe to all upcoming matches."
                : "Subscribe to matches for the selected event."}{" "}
              Your calendar app will auto-refresh as matches are added or updated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">One-click subscribe (Apple Calendar, Outlook)</p>
              <a
                href={webcalUrl}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[#1B3B6F] text-white hover:bg-[#1B3B6F]/90"
                data-testid="webcal-link"
              >
                <CalendarPlus size={14} />
                Open in Calendar app
              </a>
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">
                Google Calendar / other apps — paste this URL
              </p>
              <div className="flex gap-2">
                <Input readOnly value={subscribeUrl} className="font-mono text-xs" data-testid="subscribe-url-input" />
                <Button variant="outline" onClick={copySubscribeUrl} data-testid="copy-subscribe-url-btn">
                  <Copy size={14} className="mr-1" />
                  Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Google Calendar → <em>Other calendars</em> → <em>From URL</em> → paste this link.
              </p>
            </div>

            <div className="pt-2 border-t">
              <a
                href={subscribeUrl}
                download="trap-tracker.ics"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
                data-testid="download-ics-btn"
              >
                <Download size={14} />
                Or download a one-time .ics file
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
